import React from 'react';
import {
  FileText, Settings, Upload, BookOpen, FileUp,
  Send, RefreshCw, Play, Lightbulb, MessageSquare,
  Sliders, Layers, Brain, Globe, Languages, CheckCircle2
} from 'lucide-react';
import TheaterSelector from '@/components/TheaterSelector';

const BriefTab = ({
  token,
  activeTheater,
  setActiveTheater,
  scanBrief,
  setScanBrief,
  systemPrompt,
  setSystemPrompt,
  showPromptEditor,
  setShowPromptEditor,
  periodStart,
  setPeriodStart,
  periodEnd,
  setPeriodEnd,
  ancientReports,
  referenceDocuments,
  handleDocumentUpload,
  briefAnalyzed,
  setBriefAnalyzed,
  isAnalyzingBrief,
  analyzeBrief,
  briefSuggestions,
  briefQuestions,
  briefAnswers,
  setBriefAnswers,
  isScanning,
  startScan,
  showScanConfig,
  setShowScanConfig,
  scanConfig,
  selectedDepth,
  setSelectedDepth,
  selectedAiLevel,
  setSelectedAiLevel,
  selectedCategories,
  setSelectedCategories,
  selectedLanguages,
  setSelectedLanguages
}) => {
  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="bg-black/60 border-2 border-marine-slate/50 p-6">
        <h2 className="font-heading text-marine-cyan text-2xl uppercase tracking-wider mb-6 flex items-center gap-3">
          <FileText className="w-7 h-7" />
          Brief de Mission
        </h2>
        
        <div className="space-y-6">
          {/* Theater Selector */}
          <TheaterSelector 
            token={token}
            activeTheater={activeTheater}
            onTheaterChange={setActiveTheater}
            onBriefChange={setScanBrief}
          />
          
          {/* Brief Input */}
          <div>
            <label className="block font-mono text-white text-base uppercase tracking-wider mb-3">
              Décrivez votre besoin d'analyse
            </label>
            <textarea
              value={scanBrief}
              onChange={(e) => { setScanBrief(e.target.value); setBriefAnalyzed(false); }}
              className="w-full bg-black border-2 border-marine-cyan/50 text-white font-mono text-base p-4 focus:border-marine-cyan focus:outline-none h-36"
              placeholder="Ex: Mon centre d'études doit sortir une fiche visant à expliciter la conséquence pour la France de la fermeture du détroit d'Ormuz..."
              data-testid="brief-input"
            />
          </div>
          
          {/* System Prompt Editor */}
          <div className="border border-marine-slate/30 p-4">
            <button 
              onClick={() => setShowPromptEditor(!showPromptEditor)}
              className="flex items-center gap-2 font-mono text-marine-amber text-sm uppercase tracking-wider mb-3"
            >
              <Settings className="w-4 h-4" />
              {showPromptEditor ? 'Masquer' : 'Afficher'} le prompt système (éditable)
            </button>
            {showPromptEditor && (
              <textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                className="w-full bg-black/50 border border-marine-amber/30 text-marine-amber/80 font-mono text-sm p-3 focus:border-marine-amber focus:outline-none h-32"
              />
            )}
          </div>

          {/* Period */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-white/90 text-sm uppercase tracking-wider mb-2">Période - Début</label>
              <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full bg-black border border-marine-slate/50 text-white font-mono text-base p-3 focus:border-marine-cyan" />
            </div>
            <div>
              <label className="block font-mono text-white/90 text-sm uppercase tracking-wider mb-2">Période - Fin</label>
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full bg-black border border-marine-slate/50 text-white font-mono text-base p-3 focus:border-marine-cyan" />
            </div>
          </div>

          {/* Document References */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-marine-slate/30 p-4">
              <label className="flex items-center gap-2 font-mono text-marine-cyan text-sm uppercase tracking-wider mb-3">
                <FileUp className="w-4 h-4" />
                Rapports anciens (inspiration)
              </label>
              <input type="file" multiple onChange={(e) => handleDocumentUpload(e, 'ancient')} className="hidden" id="ancient-upload" />
              <label htmlFor="ancient-upload" className="block w-full py-3 bg-marine-slate/20 border border-dashed border-marine-slate text-marine-slate text-center cursor-pointer hover:border-marine-cyan hover:text-marine-cyan">
                <Upload className="w-5 h-5 mx-auto mb-1" />
                Glisser ou cliquer
              </label>
              {ancientReports.length > 0 && (
                <div className="mt-2 text-marine-green text-xs">{ancientReports.length} rapport(s)</div>
              )}
            </div>
            <div className="border border-marine-slate/30 p-4">
              <label className="flex items-center gap-2 font-mono text-marine-cyan text-sm uppercase tracking-wider mb-3">
                <BookOpen className="w-4 h-4" />
                Publications / Livres (socle IA)
              </label>
              <input type="file" multiple onChange={(e) => handleDocumentUpload(e, 'reference')} className="hidden" id="ref-upload" />
              <label htmlFor="ref-upload" className="block w-full py-3 bg-marine-slate/20 border border-dashed border-marine-slate text-marine-slate text-center cursor-pointer hover:border-marine-cyan hover:text-marine-cyan">
                <Upload className="w-5 h-5 mx-auto mb-1" />
                Glisser ou cliquer
              </label>
              {referenceDocuments.length > 0 && (
                <div className="mt-2 text-marine-green text-xs">{referenceDocuments.length} document(s)</div>
              )}
            </div>
          </div>
          
          {!briefAnalyzed ? (
            <button
              onClick={analyzeBrief}
              disabled={isAnalyzingBrief || !scanBrief.trim()}
              className="w-full py-5 bg-marine-amber/20 border-2 border-marine-amber text-marine-amber hover:bg-marine-amber hover:text-black font-heading text-lg uppercase tracking-widest flex items-center justify-center gap-3 disabled:opacity-50"
              data-testid="analyze-brief-btn"
            >
              {isAnalyzingBrief ? <RefreshCw className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" />}
              Analyser le Brief
            </button>
          ) : (
            <div className="space-y-6">
              {/* Suggestions */}
              <div className="p-4 bg-marine-green/10 border-2 border-marine-green/50">
                <h3 className="font-mono text-marine-green text-base font-bold mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  SUGGESTIONS - Voulez-vous aussi...?
                </h3>
                {briefSuggestions.map((sugg, idx) => (
                  <label key={idx} className="flex items-start gap-3 mb-3 cursor-pointer">
                    <input type="checkbox" className="w-5 h-5 mt-1 bg-black border-marine-green" 
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBriefAnswers({ ...briefAnswers, [`suggestion_${idx}`]: 'Oui' });
                        }
                      }}
                    />
                    <span className="font-mono text-white text-base">{sugg}</span>
                  </label>
                ))}
              </div>

              {/* Questions */}
              <div className="p-4 bg-marine-amber/10 border-2 border-marine-amber/50">
                <h3 className="font-mono text-marine-amber text-base font-bold mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  QUESTIONS COMPLÉMENTAIRES
                </h3>
                {briefQuestions.map((q, idx) => (
                  <div key={idx} className="mb-4">
                    <p className="font-mono text-white text-base mb-2">{idx + 1}. {q}</p>
                    <input
                      type="text"
                      value={briefAnswers[q] || ''}
                      onChange={(e) => setBriefAnswers({ ...briefAnswers, [q]: e.target.value })}
                      className="w-full bg-black border border-marine-slate/50 text-white font-mono text-base p-3 focus:border-marine-amber"
                      placeholder="Votre réponse..."
                    />
                  </div>
                ))}
              </div>
              
              <button
                onClick={startScan}
                disabled={isScanning}
                className="w-full py-5 bg-marine-cyan/20 border-2 border-marine-cyan text-marine-cyan hover:bg-marine-cyan hover:text-black font-heading text-lg uppercase tracking-widest flex items-center justify-center gap-3 disabled:opacity-50"
                data-testid="start-scan-btn"
              >
                {isScanning ? <RefreshCw className="w-6 h-6 animate-spin" /> : <Play className="w-6 h-6" />}
                Lancer le Scan OSINT
              </button>

              {/* Scan Configuration Panel */}
              <div className="mt-4 p-4 bg-marine-slate/10 border border-marine-slate/50">
                <button
                  onClick={() => setShowScanConfig(!showScanConfig)}
                  className="w-full flex items-center justify-between text-white/80 hover:text-white"
                >
                  <span className="font-mono text-sm uppercase tracking-wider flex items-center gap-2">
                    <Sliders className="w-4 h-4" />
                    Configuration avancée du scan
                  </span>
                  <span className="text-marine-cyan">{showScanConfig ? '▲' : '▼'}</span>
                </button>
                
                {showScanConfig && scanConfig && (
                  <div className="mt-4 space-y-4">
                    {/* Scan Depth */}
                    <div>
                      <p className="font-mono text-white/90 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Profondeur du Scan
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(scanConfig.depth_options).map(([key, opt]) => (
                          <button
                            key={key}
                            onClick={() => setSelectedDepth(key)}
                            className={`p-3 border text-left transition-all ${
                              selectedDepth === key 
                                ? 'border-marine-cyan bg-marine-cyan/20 text-marine-cyan' 
                                : 'border-marine-slate/50 text-white/90 hover:border-marine-slate'
                            }`}
                          >
                            <p className="font-mono text-sm font-bold">{opt.name}</p>
                            <p className="font-mono text-xs opacity-70">{opt.description}</p>
                            <p className="font-mono text-xs mt-1 text-marine-amber">{opt.time_estimate}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* AI Analysis Level */}
                    <div>
                      <p className="font-mono text-white/90 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        Niveau d'Analyse IA
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(scanConfig.ai_level_options).map(([key, opt]) => (
                          <button
                            key={key}
                            onClick={() => setSelectedAiLevel(key)}
                            className={`p-3 border text-left transition-all ${
                              selectedAiLevel === key 
                                ? 'border-marine-green bg-marine-green/20 text-marine-green' 
                                : 'border-marine-slate/50 text-white/90 hover:border-marine-slate'
                            }`}
                          >
                            <p className="font-mono text-sm font-bold">{opt.name}</p>
                            <p className="font-mono text-xs opacity-70">{opt.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Source Categories */}
                    <div>
                      <p className="font-mono text-white/90 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Catégories de Sources
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {scanConfig.available_categories.map(cat => (
                          <button
                            key={cat.id}
                            onClick={() => {
                              if (selectedCategories.includes(cat.id)) {
                                setSelectedCategories(selectedCategories.filter(c => c !== cat.id));
                              } else {
                                setSelectedCategories([...selectedCategories, cat.id]);
                              }
                            }}
                            className={`px-4 py-2 border flex items-center gap-2 ${
                              selectedCategories.includes(cat.id)
                                ? cat.id === 'western' ? 'border-marine-green bg-marine-green/20 text-marine-green'
                                  : cat.id === 'adversary' ? 'border-marine-red bg-marine-red/20 text-marine-red'
                                  : 'border-marine-amber bg-marine-amber/20 text-marine-amber'
                                : 'border-marine-slate/50 text-white/80'
                            }`}
                          >
                            {selectedCategories.includes(cat.id) && <CheckCircle2 className="w-4 h-4" />}
                            <span className="font-mono text-sm">{cat.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Languages */}
                    <div>
                      <p className="font-mono text-white/90 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Languages className="w-4 h-4" />
                        Langues
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {scanConfig.available_languages.map(lang => (
                          <button
                            key={lang.code}
                            onClick={() => {
                              if (selectedLanguages.includes(lang.code)) {
                                setSelectedLanguages(selectedLanguages.filter(l => l !== lang.code));
                              } else {
                                setSelectedLanguages([...selectedLanguages, lang.code]);
                              }
                            }}
                            className={`px-3 py-1 border font-mono text-sm ${
                              selectedLanguages.includes(lang.code)
                                ? 'border-marine-cyan bg-marine-cyan/20 text-marine-cyan'
                                : 'border-marine-slate/50 text-white/80'
                            }`}
                          >
                            {lang.code.toUpperCase()} - {lang.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="p-3 bg-black/50 border border-marine-cyan/30">
                      <p className="font-mono text-marine-cyan text-sm">
                        <strong>Configuration actuelle:</strong> {scanConfig.depth_options[selectedDepth]?.name} | 
                        IA: {scanConfig.ai_level_options[selectedAiLevel]?.name} | 
                        {selectedCategories.length} catégorie(s) | 
                        {selectedLanguages.length} langue(s)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BriefTab;
